/**
 * BYO chat picker (plan whatsapp-byo-flow.md §5.2). After the account is linked,
 * choose which chats — groups or DMs — to index. DMs are private by default
 * (plan M3); groups follow the selected workspace/private toggle.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Lock, MessageSquare, Search, User, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { type WahaChat, wahaSessionApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

interface Props {
  sessionId: string;
  onDone: () => void;
}

export function WahaChatPicker({ sessionId, onDone }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [groupVisibility, setGroupVisibility] = useState<'workspace' | 'private'>('workspace');

  const { data: chats, isLoading } = useQuery({
    queryKey: [...qk.wahaSession(sessionId), 'chats'],
    queryFn: () => wahaSessionApi.chats(sessionId),
  });

  const filtered = useMemo<WahaChat[]>(() => {
    const all = chats ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((ch) => (ch.name ?? ch.chatId).toLowerCase().includes(q));
  }, [chats, query]);

  const selectMut = useMutation({
    mutationFn: () => {
      const byId = new Map((chats ?? []).map((c) => [c.chatId, c]));
      const payload = [...selected].map((chatId) => {
        const kind = byId.get(chatId)?.kind ?? (chatId.endsWith('@g.us') ? 'group' : 'dm');
        return {
          chatId,
          visibility: kind === 'dm' ? ('private' as const) : groupVisibility,
        };
      });
      return wahaSessionApi.selectChats(sessionId, payload);
    },
    onSuccess: (res) => {
      toast.success(`Indexing ${res.selected} chat${res.selected === 1 ? '' : 's'}.`);
      onDone();
    },
    onError: () => toast.error('Failed to save your selection. Please try again.'),
  });

  function toggle(chatId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.chatId));

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of filtered) {
        if (allFilteredSelected) next.delete(c.chatId);
        else next.add(c.chatId);
      }
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Choose chats to index</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Pick the groups and direct chats Brain should make searchable. You can change this later.
        </p>
      </div>

      {/* Group visibility — DMs stay private regardless (M3). */}
      <div className="flex items-center gap-3 rounded-lg border border-border-subtle p-3 text-[13px]">
        <span className="text-muted-foreground">Selected groups are</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['workspace', 'private'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setGroupVisibility(v)}
              className={cn(
                'px-3 py-1 text-[12.5px] transition-colors',
                groupVisibility === v
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {v === 'workspace' ? 'Shared with workspace' : 'Private to me'}
            </button>
          ))}
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-faint">
          <Lock className="h-3 w-3" /> DMs are always private
        </span>
      </div>

      {/* Search + select all */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-faint" />
          <Input
            placeholder="Search chats…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={toggleAll} disabled={filtered.length === 0}>
          {allFilteredSelected ? 'Clear' : 'Select all'}
        </Button>
      </div>

      {/* Chat list */}
      <div className="max-h-[360px] overflow-y-auto rounded-xl border border-border">
        {filtered.length === 0 ? (
          <p className="p-6 text-center text-[13px] text-faint">No chats found.</p>
        ) : (
          filtered.map((chat) => {
            const isOn = selected.has(chat.chatId);
            return (
              <button
                key={chat.chatId}
                type="button"
                aria-pressed={isOn}
                onClick={() => toggle(chat.chatId)}
                className="flex w-full items-center gap-3 border-border-subtle border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/40"
              >
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                    isOn ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                  )}
                >
                  {isOn && <Check className="h-3.5 w-3.5" />}
                </span>
                {chat.kind === 'group' ? (
                  <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1 truncate text-[14px]">
                  {chat.name || chat.chatId.replace(/@(g\.us|c\.us)$/, '')}
                </span>
                <span className="shrink-0 text-[11.5px] text-faint uppercase tracking-[0.04em]">
                  {chat.kind === 'group' ? 'Group' : 'DM'}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center gap-3">
        <MessageSquare className="h-4 w-4 text-faint" />
        <span className="text-[13px] text-faint">{selected.size} selected</span>
        <Button
          size="cta"
          className="ml-auto"
          disabled={selected.size === 0 || selectMut.isPending}
          onClick={() => selectMut.mutate()}
        >
          {selectMut.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            `Index ${selected.size} chat${selected.size === 1 ? '' : 's'}`
          )}
        </Button>
      </div>
    </div>
  );
}
